import * as React from 'react';

import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import {
  isElectronApp,
  getCountryByIP,
  getCurrencyByCountryCode,
  isPro,
  postMessageToIosWrapper
} from '../utils';
import Menu from '../components/Menu';
import MenuItem from '../components/MenuItem';
import ButtonTooltip from '../controller/components/ButtonTooltip';
import User, { CustomFont } from '../data/User';
import { getFingerPrint } from '../utils/index';
import { identify, reportBFError } from '../utils/Tracker';
import { loadCustomFont } from 'utils/FontUtils';
import { PlatformType } from 'data/PlatformType';
import { Modal } from 'components/Modal';
import { SignIn } from 'signin/SignIn';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';

var isMonitoringConnections = false;
const monitorConnections = (userId: string) => {
  if (isMonitoringConnections) {
    return;
  }
  console.log('Monitor connections...');
  const myConnectionsRef = firebase
    .database()
    .ref(`users/${userId}/connections`);

  const connectedRef = firebase.database().ref('.info/connected');
  connectedRef.on('value', async (snap: any) => {
    if (snap.val() === true) {
      // We're connected (or reconnected)! Do anything here that should happen only if online (or on reconnect)
      const connectionsData = (await myConnectionsRef.once('value')).val();
      const currentConnections =
        ((connectionsData && Object.keys(connectionsData).length) || 0) + 1;

      console.log('Current connections: ' + currentConnections);

      if (currentConnections > 1) {
        // storeDispatch(Actions.blockUser());

        const maxConnectionsRef = firebase
          .database()
          .ref(`users/${userId}/maxConnections`);
        const maxConnections =
          (await maxConnectionsRef.once('value')).val() || 1;
        if (currentConnections > maxConnections) {
          await maxConnectionsRef.set(currentConnections);
        }
      }

      var con = await myConnectionsRef.push();

      // When I disconnect, remove this device
      con.onDisconnect().remove();

      // Add this device to my connections list
      // this value could contain info about the device or a timestamp too
      let fingerPrint = await getFingerPrint();
      con.set(fingerPrint);
    }
  });
  isMonitoringConnections = true;
};

if (firebase) {
  firebase.auth().onAuthStateChanged(async user => {
    if (user) {
      console.log('Signed in...');
      postMessageToIosWrapper('userSignedIn', { uid: user.uid });
      const ref = firebase.database().ref('users/' + user.uid);
      const data: User | null = (await ref.once('value')).val();
      let countryCode: string | null;
      let currency: string;
      const nowStr = new Date().toISOString();
      if (!data) {
        // new user
        // countryCode = await getCountryByIP();
        // currency = await getCurrencyByCountryCode(countryCode);
        // const referrer = getValueFromLocalStorage(REFERRER_STORAGE_KEY);
        // ref.set({
        //   name: user.displayName,
        //   email: user.email,
        //   plan: Plan.Free,
        //   usedDesktopApp: isElectronApp(),
        //   countryCode,
        //   currency,
        //   ref: referrer !== 'direct' ? referrer : null,
        //   signedUpDate: nowStr,
        //   lastSignedInDate: nowStr
        // });
        // user.plan = Plan.Free;
        // user.countryCode = countryCode!;
        // user.currency = currency;
        // user.customResolutions = [];
      } else {
        // check country and add if missing
        countryCode = data.countryCode;
        if (!countryCode) {
          countryCode = await getCountryByIP();
        }
        currency = await getCurrencyByCountryCode(countryCode);
        ref.update({
          name: (data as any).name ?? user.displayName,
          email: user.email,
          usedDesktopApp: data.usedDesktopApp || isElectronApp(),
          countryCode,
          currency,
          lastSignedInDate: nowStr
        });
        data.countryCode = countryCode!;
        data.currency = currency;
        if (isPro(data)) {
          try {
            await Promise.all(
              ((data.customFonts ?? []) as CustomFont[]).map(f =>
                loadCustomFont(f)
              )
            );
          } catch (e) {
            console.error(e);
          }
          if (data.segmentationSurvey === undefined) {
            storeDispatch(Actions.showSegmentationSurvey());
          }
        }
      }
      (user as unknown as User).isBlocked = false;
      storeDispatch(Actions.signInUser(user as unknown as User));
      if (data) {
        storeDispatch(Actions.updateUser(data));
        if (data.monitor) {
          monitorConnections(user.uid);
        }
      }
      if (user.email) {
        identify(user.email);
      }
    } else {
      console.log('User signed out');
      storeDispatch(Actions.signOutUser());
    }
  });
}

export const UserButton: React.FC = () => {
  const [showSignIn, setShowSignIn] = React.useState(false);
  const [showAccountMenu, setShowAccountMenu] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const signIn = React.useCallback(() => {
    setShowSignIn(true);
  }, []);

  const openMenu = React.useCallback(() => {
    setShowAccountMenu(true);
  }, []);

  const closeMenu = React.useCallback(() => {
    setShowAccountMenu(false);
  }, []);

  const signOut = React.useCallback(() => {
    firebase
      .auth()
      .signOut()
      .then(
        () => {
          console.log('Signed Out');
          // this.props.onSignOut();
        },
        (error: Error) => {
          reportBFError(error);
        }
      );
  }, []);

  const onMenuClicked = React.useCallback(
    (key: string) => {
      switch (key) {
        case 'signout':
          signOut();
          break;
        case 'settings':
          storeDispatch(Actions.showAccountSettings());
          break;
        default:
          break;
      }
      closeMenu();
    },
    [closeMenu, signOut]
  );

  const { user, isAutoLogging, platform } = getStoreState().app;

  let className = 'large secondary';
  let label: string | React.ReactNode = 'Sign in';
  if (isAutoLogging) {
    className = 'tertiary avatar';
    label = (
      <img
        alt=""
        src="/media/icons/signing-in.svg"
        crossOrigin="anonymous"
        draggable={false}
      />
    );
  } else if (user) {
    className = 'tertiary avatar';
    label = (
      <img
        alt=""
        src={user.photoURL || '/media/icons/avatar.svg'}
        crossOrigin="anonymous"
        draggable={false}
      />
    );
  }
  return (
    <div style={{ height: 'calc( var(--top-bar-height) - 20px )' }}>
      <ButtonTooltip
        text={user ? 'Account' : ''}
        hide={user === null || showAccountMenu}
        placement="bottom"
      >
        <button
          className={className}
          onClick={user ? openMenu : signIn}
          disabled={isAutoLogging}
          ref={buttonRef}
        >
          {label}
        </button>
      </ButtonTooltip>
      {showAccountMenu && buttonRef.current && (
        <Menu
          getParent={() => buttonRef.current}
          onClose={closeMenu}
          onClick={onMenuClicked}
          rightAligned={true}
        >
          {platform !== PlatformType.iOSApp && (
            <MenuItem actionKey="settings">Account settings</MenuItem>
          )}
          <MenuItem actionKey="signout" className="red">
            Sign out
          </MenuItem>
        </Menu>
      )}
      {showSignIn && (
        <Modal>
          <SignIn onClose={() => setShowSignIn(false)} />
        </Modal>
      )}
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/showcase/UserButton.tsx